"use client";

import React, { FC, useEffect, useRef, useState } from "react";

import { ClassicSideMenu } from "~/components/header/components/header-mobile/side-menu/classic-side-menu/component";
import { supportsPopover as supportsNativePopover } from "~/helpers/supports-popover";
import { getTestAutomationProps } from "~/helpers/test-automation-props";

import { HeaderToolbar } from "../header-toolbar/component";

import { MOBILE_MENU_POPOVER_ID } from "./constants";
import { SlideMenuPopover } from "./styled";
import { SideMenuProps } from "./types";

export type { SideMenuProps } from "./types";

export const SideMenu: FC<SideMenuProps> = ({
  logo,
  isOpen,
  onOpen,
  onClose,
  children,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [supportsPopover, setSupportsPopover] = useState<boolean>();
  const hasSynchronizedInitialState = useRef(false);

  useEffect(() => {
    setSupportsPopover(supportsNativePopover());
  }, []);

  useEffect(() => {
    const popover = popoverRef.current;
    if (!popover || !supportsPopover) {
      return;
    }

    const isPopoverOpen = popover.matches(":popover-open");

    if (!hasSynchronizedInitialState.current) {
      hasSynchronizedInitialState.current = true;
      if (isPopoverOpen) {
        onOpen?.();
        return;
      }
    }

    if (isOpen && !isPopoverOpen) {
      popover.showPopover();
    } else if (!isOpen && isPopoverOpen) {
      popover.hidePopover();
    }
  }, [isOpen, onOpen, supportsPopover]);

  if (supportsPopover === false) {
    return (
      <ClassicSideMenu logo={logo} isOpen={isOpen} onClose={onClose}>
        {children}
      </ClassicSideMenu>
    );
  }

  const handleToggle = (event: React.ToggleEvent<HTMLDivElement>) => {
    const popover = event.currentTarget;

    setTimeout(() => {
      if (popover.matches(":popover-open")) {
        onOpen?.();
      } else if (isOpen) {
        onClose?.();
      }
    }, 0);
  };

  return (
    <SlideMenuPopover
      ref={popoverRef}
      id={MOBILE_MENU_POPOVER_ID}
      popover="auto"
      role="dialog"
      aria-label="Navigation menu"
      onToggle={handleToggle}
      {...getTestAutomationProps("side-menu")}
    >
      <HeaderToolbar
        logo={logo}
        isMenuOpen
        onMenuOpen={onClose}
        menuPopoverTarget={MOBILE_MENU_POPOVER_ID}
        menuPopoverTargetAction="hide"
      />
      {children}
    </SlideMenuPopover>
  );
};
